import { appConstants } from '@/appConstants';
import { SecureLinkParams } from '@/types';
import { TokenService, EnvironmentService } from '@/services';
import type { Address } from '@authvia-v3/pinia-stores-user';
import { mobileNumberFormat } from '@/composables/addressValidate';

import { clearScopedLinkContext, isScopedBusinessProcessId } from './scopedLinkSession';

export class LocalAuthHandler {
  /**
   * Clear token from session, local and indexDB storage
   */
  public static async clearToken(): Promise<void> {
    const tokenService = new TokenService();
    sessionStorage.removeItem(appConstants.AUTH_TOKEN_NAME);
    localStorage.removeItem(appConstants.AUTH_TOKEN_NAME);
    clearScopedLinkContext();
    await tokenService.removeApplicationToken(EnvironmentService.getApplicationId());
  }

  /**
   * Get the token from localStorage, falling back to sessionStorage.
   * @returns token | undefined
   */
  public static getToken(): string | undefined {
    return (
      localStorage.getItem(appConstants.AUTH_TOKEN_NAME) ||
      sessionStorage.getItem(appConstants.AUTH_TOKEN_NAME) ||
      undefined
    );
  }

  /**
   * Removes a scoped business-process token from localStorage when present.
   * Full wallet (OTP) tokens without a BP claim are left unchanged.
   */
  public static clearLocalStorageTokenIfScoped(): void {
    const token: string | null = localStorage.getItem(appConstants.AUTH_TOKEN_NAME);
    if (!token) {
      return;
    }

    try {
      const payload: Record<string, string> = TokenService.payload(token);
      const businessProcessId: string | undefined =
        payload[appConstants.MERCHANT_LINK_JWT_CLAIM_KEYS.BUSINESS_PROCESS];
      if (isScopedBusinessProcessId(businessProcessId)) {
        localStorage.removeItem(appConstants.AUTH_TOKEN_NAME);
      }
    } catch {
      localStorage.removeItem(appConstants.AUTH_TOKEN_NAME);
    }
  }

  /**
   * Stores a merchant-link exchanged token in sessionStorage only.
   * Clears any stale scoped token from localStorage before writing.
   *
   * @param token - Session token from merchant-link exchange
   */
  public static setMerchantLinkToken(token: string): void {
    LocalAuthHandler.clearLocalStorageTokenIfScoped();
    localStorage.removeItem(appConstants.AUTH_TOKEN_NAME);
    sessionStorage.setItem(appConstants.AUTH_TOKEN_NAME, token);
  }

  /**
   * Save a token in session or local storage.
   * Scoped business-process tokens use sessionStorage; full wallet tokens with
   * payment_methods:read use localStorage.
   *
   * @param token to store
   */
  public static setToken(token: string): void {
    let businessProcessId: string | undefined;
    try {
      const payload: Record<string, string> = TokenService.payload(token);
      businessProcessId = payload[appConstants.MERCHANT_LINK_JWT_CLAIM_KEYS.BUSINESS_PROCESS];
    } catch {
      sessionStorage.setItem(appConstants.AUTH_TOKEN_NAME, token);
      localStorage.removeItem(appConstants.AUTH_TOKEN_NAME);
      return;
    }

    if (isScopedBusinessProcessId(businessProcessId)) {
      localStorage.removeItem(appConstants.AUTH_TOKEN_NAME);
      sessionStorage.setItem(appConstants.AUTH_TOKEN_NAME, token);
      return;
    }

    if (LocalAuthHandler.hasScopes(token, ['payment_methods:read'])) {
      sessionStorage.removeItem(appConstants.AUTH_TOKEN_NAME);
      localStorage.setItem(appConstants.AUTH_TOKEN_NAME, token);
    } else {
      localStorage.removeItem(appConstants.AUTH_TOKEN_NAME);
      sessionStorage.setItem(appConstants.AUTH_TOKEN_NAME, token);
    }
  }

  /**
   * Store secure-link information in session storage
   * @param secureLink
   */
  public static setSecureLink(secureLink: SecureLinkParams) {
    sessionStorage.setItem(
      appConstants.SECURE_LINK_NAME,
      JSON.stringify(secureLink)
    );
  }

  /**
   * Clear secure-link information
   */
  public static clearSecureLinkData() {
    sessionStorage.removeItem(appConstants.SECURE_LINK_NAME);
  }

  /**
   * Get secure-link information
   * @returns
   */
  public static getSecureLink(): SecureLinkParams | undefined {
    const item: string | undefined = sessionStorage.getItem(appConstants.SECURE_LINK_NAME) as string | undefined;
    if (item) return JSON.parse(item) as any as SecureLinkParams;
    return undefined;
  }

  public static setRedirectPath(path: string) {
    sessionStorage.setItem(appConstants.REDIRECT_PATH_NAME, path);
  }

  public static getRedirectPath(): string | undefined {
    const path: string | undefined = sessionStorage.getItem(appConstants.REDIRECT_PATH_NAME) as string | undefined;
    return path ? path : undefined;
  }

  /**
   * Remove the redirect path
   */
  public static removeRedirectPath() {
    sessionStorage.removeItem(appConstants.REDIRECT_PATH_NAME);
  }

  /**
   * Set the initial route the router should point to
   * @param path to route to
   */
  public static setInitialPath(path: string) {
    sessionStorage.setItem(appConstants.INITIAL_PATH_NAME, path);
  }

  public static removeInitialPath() {
    sessionStorage.removeItem(appConstants.INITIAL_PATH_NAME);
  }

  /**
   * Get the initial route path
   * @returns
   */
  public static getInitialPath(): string | undefined {
    const path: string | undefined = sessionStorage.getItem(appConstants.INITIAL_PATH_NAME) as string | undefined;
    return path ? path : undefined;
  }

  public static hasScopes(token: string, scopes: string[]): boolean {
    const tokenService = new TokenService();
    try {
      tokenService.validate(token, {
        scope: scopes.join(' '),
      });
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Save a variable in the local storage so that it is coming from trigger
   * @param token to store
   */
  public static setBPInitialPath() {
    localStorage.setItem('hasTrigger', 'true');
  }

  /**
   * Get trigger information
   * @returns
   */
  public static getBPInitialPath(): string | undefined {
    return localStorage.getItem('hasTrigger') || undefined;
  }

  /* Remove trigger information
   * @returns
   */
  public static removeBPInitialPath() {
    localStorage.removeItem('hasTrigger');
  }

  /**
   * Persist the active login address for App.vue hydration after refresh.
   * Uses localStorage directly so the write is not lost if a VueUse-bound
   * component unmounts before its storage watch flushes (e.g. scoped BP modal).
   */
  public static setLockAddress(address: Address): void {
    localStorage.setItem('AUTHVIA_LOCK_ADDRESS', JSON.stringify(address));
  }

  /**
   * Persist a claimed address from a raw email/phone string (e.g. merchant-link
   * `?address=`). Phones are stored in the same display form OTP login uses
   * (`(858) 898-1858`). No-op when empty. Does not touch tokens or exchange.
   */
  public static setLockAddressFromValue(addressValue: string): void {
    const trimmed: string = addressValue.trim();
    if (!trimmed) {
      return;
    }
    const isEmail: boolean = trimmed.includes('@');
    this.setLockAddress({
      type: isEmail ? 'email' : 'phone',
      value: isEmail ? trimmed.toLowerCase() : mobileNumberFormat(trimmed),
      status: 'claimed',
    });
  }

  /**
   * Clear the persisted login address used for header/account display.
   */
  public static clearLockAddress(): void {
    localStorage.removeItem('AUTHVIA_LOCK_ADDRESS');
  }

  /**
   * Read the persisted login address, if any.
   */
  public static getLockAddress(): Address | undefined {
    const raw: string | null = localStorage.getItem('AUTHVIA_LOCK_ADDRESS');
    if (!raw) {
      return undefined;
    }
    try {
      return JSON.parse(raw) as Address;
    } catch {
      return undefined;
    }
  }

  /**
   * Save address from query parameters
   * @param address to store
   */
  public static setAddressFromQuery(address: string) {
    localStorage.setItem('AUTHVIA_LOCK_ADDRESS_QUERY', address);
  }

  /**
   * Get address from query parameters
   * @returns address | undefined
   */
  public static getAddressFromQuery(): string | undefined {
    return localStorage.getItem('AUTHVIA_LOCK_ADDRESS_QUERY') || undefined;
  }

  /**
   * Remove address from query parameters
   */
  public static removeAddressFromQuery() {
    localStorage.removeItem('AUTHVIA_LOCK_ADDRESS_QUERY');
  }

  /**
   * Sync `AUTHVIA_LOCK_ADDRESS_QUERY` from the current URL search.
   * Sets the cache when `address` is present; clears it when absent so a later
   * link cannot reuse a previous visitor's email or phone.
   *
   * @param search - URL search string (defaults to `window.location.search`)
   * @returns address from this URL, or undefined
   */
  public static syncAddressQueryFromSearch(
    search: string = typeof window !== 'undefined' ? window.location.search : '',
  ): string | undefined {
    try {
      const match: RegExpMatchArray | null = search.match(/[?&]address=([^&]*)/);
      const raw: string | undefined = match ? decodeURIComponent(match[1]) : undefined;
      const addressParam: string | undefined = raw?.trim() || undefined;
      if (addressParam) {
        this.setAddressFromQuery(addressParam);
        return addressParam;
      }
      this.removeAddressFromQuery();
      return undefined;
    } catch {
      this.removeAddressFromQuery();
      return undefined;
    }
  }
}
